import { profileTitle, type HermesAPI, type ProfileSummary } from "@timibot/core";
import { Observable } from "./observable";

/** Hermes 的 Bot（profile）名册。每个 profile 就是一个有独立人设、模型、技能的机器人。 */
export class ProfileStore extends Observable {
  profiles: ProfileSummary[] = [];
  botModeProtocol = false;
  avatars = new Map<string, string>();
  isLoading = false;
  private avatarRequests = new Set<string>();

  async refresh(api: HermesAPI | undefined): Promise<void> {
    if (!api) return;
    this.isLoading = true;
    this.changed();
    try {
      const result = await api.listProfiles();
      this.profiles = [...result.profiles].sort((a, b) => {
        if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
        return profileTitle(a).localeCompare(profileTitle(b), "zh-Hans-CN", { numeric: true });
      });
      this.botModeProtocol = result.botModeProtocol;
    } catch {
      // 老版本网关没有 profiles.list：只显示默认 Bot
      if (!this.profiles.length) this.profiles = [{ name: "default", is_default: true, has_avatar: false }];
    } finally {
      this.isLoading = false;
      this.changed();
    }
  }

  /** undefined 表示默认 profile */
  profile(named: string | undefined): ProfileSummary | undefined {
    if (named === undefined) return this.profiles.find((p) => p.is_default);
    return this.profiles.find((p) => p.name === named);
  }

  title(name: string | undefined): string {
    const p = this.profile(name);
    return p ? profileTitle(p) : (name ?? "Hermes");
  }

  /** profile → 会话 API 用的 profile 参数（默认 profile 传 undefined） */
  apiName(p: ProfileSummary): string | undefined {
    return p.is_default ? undefined : p.name;
  }

  async loadAvatar(p: ProfileSummary, api: HermesAPI | undefined): Promise<void> {
    if (!p.has_avatar || this.avatars.has(p.name) || this.avatarRequests.has(p.name) || !api) return;
    this.avatarRequests.add(p.name);
    try {
      const url = await api.profileAvatar(p.name);
      if (url) { this.avatars.set(p.name, url); this.changed(); }
    } catch { /* 没有头像就用首字母 */ }
  }

  clear(): void {
    this.profiles = [];
    this.avatars = new Map();
    this.avatarRequests = new Set();
    this.changed();
  }
}
